import { API_URL } from '../config/api';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

// Brand configuration
const BRAND_CONFIG: Record<string, {
  name: string;
  description: string;
  color: string;
  colorHover: string;
  colorShadow: string;
}> = {
  ssemble: {
    name: 'Ssemble',
    description: 'Ssemble is looking to collaborate with talented creators like you! If you\'re interested in working with us, please fill out this short form.',
    color: '#ff6b35',
    colorHover: '#ff5722',
    colorShadow: 'rgba(255, 107, 53, 0.3)',
  },
};

const DEFAULT_BRAND = 'ssemble';

const CollaborationInquiry: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { brand } = useParams<{ brand: string }>();

  // Get brand config with fallback to default
  const brandConfig = BRAND_CONFIG[brand || ''] || BRAND_CONFIG[DEFAULT_BRAND];
  const currentBrand = brand && BRAND_CONFIG[brand] ? brand : DEFAULT_BRAND;

  // Get email from URL parameter
  const searchParams = new URLSearchParams(location.search);
  const emailFromUrl = searchParams.get('email') || '';
  
  const [formData, setFormData] = useState({
    email: emailFromUrl,
    platform: '',
    handle: '',
    price: '',
    videoDeletePolicy: false,
    paypalPaymentAgreement: false,
    audienceCountryFile: null as File | null
  });

  // File upload states
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [extractedCountries, setExtractedCountries] = useState<Record<string, number> | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailFromUrl, setIsEmailFromUrl] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'error' | 'success' | 'info' | 'warning'>('error');
  const [isDragOver, setIsDragOver] = useState(false);

  // 로딩 페이지에서 돌아온 에러 처리
  useEffect(() => {
    const errorMessage = location.state?.error;
    if (errorMessage) {
      showToastMessage(errorMessage, 'error');
      // Clear the error state
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.state?.error]);

  // Update email when URL parameter changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setFormData(prev => ({
        ...prev,
        email: emailParam
      }));
      setIsEmailFromUrl(true); // Mark that email came from URL
    }
  }, [location.search]);

  // Auto-hide toast after 5 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const showToastMessage = (message: string, type: 'error' | 'success' | 'info' | 'warning' = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'price') {
      // Only allow numbers and one decimal point for price field
      const numericValue = (value || '').replace(/[^0-9.]/g, '');
      // Prevent multiple decimal points
      const parts = numericValue.split('.');
      const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else if (name === 'handle') {
      // Handle formatting: lowercase, no spaces, Instagram/TikTok allowed characters only
      let cleanValue = (value || '').toLowerCase().replace(/\s/g, ''); // Remove spaces and convert to lowercase
      
      // Remove @ at the beginning if user typed it
      if (cleanValue.startsWith('@')) {
        cleanValue = cleanValue.substring(1);
      }
      
      // Only allow letters, numbers, underscore, and period (Instagram/TikTok handle rules)
      cleanValue = cleanValue.replace(/[^a-z0-9_.]/g, '');
      
      // Instagram/TikTok handle length limit (typically 30 characters)
      if (cleanValue.length > 30) {
        cleanValue = cleanValue.substring(0, 30);
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: cleanValue
      }));
    } else if (name === 'email') {
      // Basic email format validation
      const emailValue = (value || '').toLowerCase().trim();
      setFormData(prev => ({
        ...prev,
        [name]: emailValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateFile = (file: File): boolean => {
    // File size validation (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showToastMessage('File size must be less than 10MB', 'error');
      return false;
    }

    // File type validation
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      showToastMessage('Please upload an image (JPG, PNG, GIF, WebP) or PDF file only', 'error');
      return false;
    }

    return true;
  };

  // Upload file to Azure immediately when selected
  const uploadFileToAzure = async (file: File): Promise<void> => {
    setUploadStatus('uploading');
    setUploadError(null);

    const uploadFormData = new FormData();
    uploadFormData.append('audienceCountryFile', file);
    uploadFormData.append('handle', formData.handle || 'pending');

    try {
      const response = await fetch(`${API_URL}/api/responses/upload-audience`, {
        method: 'POST',
        body: uploadFormData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }

      const data = await response.json();

      if (data.success) {
        setUploadedFileUrl(data.data.url);
        setExtractedCountries(data.data.extractedCountries);
        setUploadStatus('success');
        showToastMessage('File uploaded successfully!', 'success');
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('File upload error:', error);
      setUploadStatus('error');
      setUploadError(error instanceof Error ? error.message : 'Failed to upload file');
      showToastMessage(
        error instanceof Error ? error.message : 'Failed to upload file. Please try again.',
        'error'
      );
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file && validateFile(file)) {
      setFormData(prev => ({
        ...prev,
        audienceCountryFile: file
      }));

      // Upload file immediately
      await uploadFileToAzure(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setFormData(prev => ({
          ...prev,
          audienceCountryFile: file
        }));

        // Upload file immediately
        await uploadFileToAzure(file);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.email || !formData.platform || !formData.handle || !formData.price) {
        throw new Error('Please fill in all required fields');
      }

      // Validate audience country file upload
      if (!uploadedFileUrl) {
        throw new Error('Please upload an audience country demographics file');
      }

      if (uploadStatus === 'uploading') {
        throw new Error('Please wait for the file upload to complete');
      }

      if (uploadStatus === 'error') {
        throw new Error('File upload failed. Please try uploading again');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Validate handle
      if (formData.handle.length < 1) {
        throw new Error('Handle cannot be empty');
      }
      if (formData.handle.length > 30) {
        throw new Error('Handle must be 30 characters or less');
      }

      // Validate price
      if (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) {
        throw new Error('Please enter a valid price');
      }

      const submitData = {
        email: formData.email,
        platform: formData.platform,
        handle: formData.handle,
        price: formData.price,
        videoDeletePolicy: formData.videoDeletePolicy ? 'yes' : 'no',
        paypalPaymentAgreement: formData.paypalPaymentAgreement ? 'yes' : 'no',
        audienceFileUrl: uploadedFileUrl,
        extractedCountries: extractedCountries,
        brand: currentBrand
      };

      // Submit to API and wait for response
      const response = await fetch(`${API_URL}/api/responses/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit inquiry');
      }

      const result = await response.json();

      if (result.success) {
        // Navigate to thank you page on success
        navigate('/thank-you', {
          state: {
            platform: formData.platform,
            handle: formData.handle,
            email: formData.email,
            responseId: result.data.responseId
          }
        });
      } else {
        throw new Error(result.error || 'Failed to submit inquiry');
      }

    } catch (error) {
      showToastMessage(error instanceof Error ? error.message : 'Failed to submit inquiry', 'error');
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        padding: '2rem',
        border: '1px solid #e1e8ed'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: '0.5rem'
          }}>
            Collaboration Inquiry
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: '#666',
            marginBottom: '1rem'
          }}>
            Hello, Creators! 🎬
          </p>
          <p style={{
            fontSize: '1rem',
            color: '#555',
            lineHeight: '1.6'
          }}>
            {brandConfig.description} 🙌
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Email <span style={{ color: '#e74c3c' }}>*</span>
              {isEmailFromUrl && (
                <span style={{ 
                  color: '#28a745', 
                  fontSize: '0.9rem', 
                  fontWeight: '400',
                  marginLeft: '0.5rem'
                }}>
                  (Pre-filled from invitation)
                </span>
              )}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your.email@example.com"
              required
              disabled={isEmailFromUrl}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `2px solid ${isEmailFromUrl ? '#28a745' : '#e1e8ed'}`,
                borderRadius: '6px',
                fontSize: '1rem',
                background: isEmailFromUrl ? '#f8f9fa' : 'white',
                color: isEmailFromUrl ? '#495057' : '#333',
                cursor: isEmailFromUrl ? 'not-allowed' : 'text'
              }}
            />
          </div>

          {/* Platform */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Platform <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <select
              name="platform"
              value={formData.platform}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e1e8ed',
                borderRadius: '6px',
                fontSize: '1rem',
                background: 'white'
              }}
            >
              <option value="" disabled>Select Platform</option>
              <option value="tiktok">TikTok</option>
              <option value="instagram">Instagram</option>
              <option value="youtube">YouTube</option>
            </select>
            <p style={{
              fontSize: '0.85rem',
              color: '#666',
              marginTop: '0.5rem',
              fontStyle: 'italic'
            }}>
              If you'd like to submit for both TikTok and Instagram, please provide each account separately with its own account name, price, and audience response. You can submit additional entries at{' '}
              <a
                href={`https://dstribute.ai/inquiry/${currentBrand}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#007bff',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                {`https://dstribute.ai/inquiry/${currentBrand}`}
              </a>
            </p>
          </div>

          {/* Handle */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Handle <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#666',
                fontSize: '1rem',
                pointerEvents: 'none'
              }}>
                @
              </span>
              <input
                type="text"
                name="handle"
                value={formData.handle}
                onChange={handleInputChange}
                placeholder="username"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 24px',
                  border: '2px solid #e1e8ed',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              />
            </div>
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              marginTop: '0.25rem'
            }}>
              Lowercase letters, numbers, underscore (_), and period (.) only. Max 30 characters.
            </p>
          </div>

          {/* Price */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Price <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#666',
                fontSize: '1rem',
                pointerEvents: 'none'
              }}>
                $
              </span>
              <input
                type="text"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                placeholder="50"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 24px',
                  border: '2px solid #e1e8ed',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              />
            </div>
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              marginTop: '0.25rem'
            }}>
              Rate in <strong>USD ($)</strong> per 1 minute video Reels or TikTok post
            </p>
          </div>

          {/* Audience Country */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Audience Country <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              marginBottom: '0.5rem'
            }}>
              Screenshot of audience country demographics. Please attach the image (JPG, PNG, GIF, WebP) or PDF file directly. <strong>Max 10MB</strong>.
            </p>
            
            {/* Guide Image */}
            <div style={{
              marginBottom: '1rem',
              padding: '1rem',
              background: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.5rem' }}>
                📋 <strong>Example:</strong> How to find audience demographics
              </p>
              <img 
                src="https://storage.tally.so/ac05fa4e-95ab-4b33-bd00-202acfaada86/unnamed.png" 
                alt="Audience demographics example"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            {/* Drag and Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('audienceCountryFile')?.click()}
              style={{
                width: '100%',
                minHeight: '120px',
                border: `2px dashed ${isDragOver ? brandConfig.color : (formData.audienceCountryFile ? '#28a745' : '#e1e8ed')}`,
                borderRadius: '8px',
                background: isDragOver ? '#fff5f0' : (formData.audienceCountryFile ? '#f8fff9' : '#fafbfc'),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                padding: '1rem',
                position: 'relative'
              }}
            >
              {uploadStatus === 'uploading' ? (
                <div style={{
                  textAlign: 'center',
                  color: brandConfig.color
                }}>
                  <div style={{
                    fontSize: '2rem',
                    marginBottom: '0.5rem',
                    animation: 'spin 2s linear infinite'
                  }}>
                    ⏳
                  </div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    Uploading file...
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    Please wait while we upload your file
                  </div>
                </div>
              ) : uploadStatus === 'success' ? (
                <div style={{
                  textAlign: 'center',
                  color: '#28a745'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    File uploaded successfully!
                  </div>
                  {formData.audienceCountryFile && (
                    <>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>
                        {formData.audienceCountryFile.name}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        {(formData.audienceCountryFile.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </>
                  )}
                  {extractedCountries && Object.keys(extractedCountries).length > 0 && (
                    <div style={{
                      fontSize: '0.85rem',
                      color: '#28a745',
                      marginTop: '0.5rem'
                    }}>
                      ✓ Audience data extracted
                    </div>
                  )}
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#999',
                    marginTop: '0.5rem',
                    fontStyle: 'italic'
                  }}>
                    Click to upload a different file
                  </div>
                </div>
              ) : uploadStatus === 'error' ? (
                <div style={{
                  textAlign: 'center',
                  color: '#dc3545'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>❌</div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    Upload failed
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#dc3545' }}>
                    {uploadError || 'Failed to upload file'}
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#666',
                    marginTop: '0.5rem'
                  }}>
                    Click to try again
                  </div>
                </div>
              ) : formData.audienceCountryFile ? (
                <div style={{
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📄</div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    {formData.audienceCountryFile.name}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    {(formData.audienceCountryFile.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: brandConfig.color,
                    marginTop: '0.5rem'
                  }}>
                    File selected - uploading will start automatically
                  </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  color: isDragOver ? brandConfig.color : '#666'
                }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>
                    {isDragOver ? '📁' : '📎'}
                  </div>
                  <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                    {isDragOver ? 'Drop your file here' : 'Drag & drop your file here'}
                  </div>
                  <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    or <span style={{ color: brandConfig.color, textDecoration: 'underline' }}>click to browse</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#999' }}>
                    Supports: JPG, PNG, GIF, WebP, PDF (Max 10MB)
                  </div>
                </div>
              )}
            </div>
            
            <input
              type="file"
              id="audienceCountryFile"
              name="audienceCountryFile"
              onChange={handleFileChange}
              accept="image/*,.pdf"
              style={{
                display: 'none'
              }}
            />
          </div>

          {/* Video Delete Policy */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              If the video is deleted within 2 months of being posted, would you be willing to re-upload it or issue a refund? <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="videoDeletePolicy"
                name="videoDeletePolicy"
                checked={formData.videoDeletePolicy}
                onChange={handleInputChange}
                style={{
                  width: '18px',
                  height: '18px',
                  marginTop: '2px',
                  cursor: 'pointer'
                }}
              />
              <label htmlFor="videoDeletePolicy" style={{ 
                color: '#333',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                Yes, I am willing to re-upload it or issue a refund
              </label>
            </div>
          </div>

          {/* PayPal Payment Agreement */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              I agree to receive collaboration payment via PayPal. <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="paypalPaymentAgreement"
                name="paypalPaymentAgreement"
                checked={formData.paypalPaymentAgreement}
                onChange={handleInputChange}
                style={{
                  width: '18px',
                  height: '18px',
                  marginTop: '2px',
                  cursor: 'pointer'
                }}
              />
              <label htmlFor="paypalPaymentAgreement" style={{ 
                color: '#333',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                I agree to receive payment via PayPal.
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={
              isSubmitting ||
              !formData.email ||
              !formData.platform ||
              !formData.handle ||
              !formData.price ||
              uploadStatus !== 'success' || // File must be uploaded successfully
              !uploadedFileUrl || // Must have uploaded file URL
              !formData.videoDeletePolicy ||
              !formData.paypalPaymentAgreement
            }
            style={{
              width: '100%',
              padding: '1rem',
              background: (
                isSubmitting ||
                !formData.email ||
                !formData.platform ||
                !formData.handle ||
                !formData.price ||
                uploadStatus !== 'success' ||
                !uploadedFileUrl ||
                !formData.videoDeletePolicy ||
                !formData.paypalPaymentAgreement
              ) ? '#95a5a6' : brandConfig.color,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: (
                isSubmitting ||
                !formData.email ||
                !formData.platform ||
                !formData.handle ||
                !formData.price ||
                uploadStatus !== 'success' ||
                !uploadedFileUrl ||
                !formData.videoDeletePolicy ||
                !formData.paypalPaymentAgreement
              ) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: (
                isSubmitting ||
                !formData.email ||
                !formData.platform ||
                !formData.handle ||
                !formData.price ||
                uploadStatus !== 'success' ||
                !uploadedFileUrl ||
                !formData.videoDeletePolicy ||
                !formData.paypalPaymentAgreement
              ) ? 'none' : `0 4px 15px ${brandConfig.colorShadow}`,
              opacity: (
                !formData.email ||
                !formData.platform ||
                !formData.handle ||
                !formData.price ||
                uploadStatus !== 'success' ||
                !uploadedFileUrl ||
                !formData.videoDeletePolicy ||
                !formData.paypalPaymentAgreement
              ) ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (
                !isSubmitting &&
                formData.email &&
                formData.platform &&
                formData.handle &&
                formData.price &&
                uploadStatus === 'success' &&
                uploadedFileUrl &&
                formData.videoDeletePolicy &&
                formData.paypalPaymentAgreement
              ) {
                e.currentTarget.style.background = brandConfig.colorHover;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 6px 20px ${brandConfig.colorShadow.replace('0.3', '0.4')}`;
              }
            }}
            onMouseLeave={(e) => {
              if (
                !isSubmitting &&
                formData.email &&
                formData.platform &&
                formData.handle &&
                formData.price &&
                uploadStatus === 'success' &&
                uploadedFileUrl &&
                formData.videoDeletePolicy &&
                formData.paypalPaymentAgreement
              ) {
                e.currentTarget.style.background = brandConfig.color;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 15px ${brandConfig.colorShadow}`;
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          minWidth: '300px',
          maxWidth: '600px',
          padding: '16px 24px',
          background: toastType === 'error' ? '#dc3545' : 
                      toastType === 'success' ? '#28a745' :
                      toastType === 'warning' ? '#ffc107' : '#17a2b8',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          zIndex: 9999,
          animation: 'slideUp 0.3s ease-out',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flex: 1
          }}>
            <span style={{ fontSize: '20px' }}>
              {toastType === 'error' ? '⚠️' : 
               toastType === 'success' ? '✅' :
               toastType === 'warning' ? '⚠️' : 'ℹ️'}
            </span>
            <span style={{
              fontSize: '15px',
              fontWeight: '500',
              lineHeight: '1.4'
            }}>
              {toastMessage}
            </span>
          </div>
          <button
            onClick={() => setShowToast(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
          >
            ✕
          </button>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default CollaborationInquiry;